import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { 
  Typography, 
  Button, 
  Table, 
  Tooltip, 
  Input, 
  Page,
  Select
} from "../../../design-system";
import Modal from "../../../_components/Modal";
import { filter } from "../../../api/nfce";
import { formatCurrencyValue, formatDate } from "../../../utils";
import * as colors from "../../../assets/_base/colors";
import DownloadReport from "../NFe/List/DownloadReport";
import { cancelSale } from "../../../api/sales";
import { getNFeBase64 } from "../../../api/nfe";

const NFCE_STATUS_CHOICES = {
  "1": "Autorizado",
  "2": "Cancelado",
  "3": "Rejeitado",
  "4": "Inutilizado",
};

const NFCE_STATUS_COLORS = {
  "1": colors.success,
  "2": colors.dark,
  "3": colors.danger,
  "4": colors.warn,
};

const NFCE_STATUS_OPTIONS = [
  { value: "0", label: "Todas" },
  { value: "1", label: "Autorizado" },
  { value: "2", label: "Cancelado" },
  { value: "3", label: "Rejeitado" },
  { value: "4", label: "Inutilizado" },
];

const NFCeList = () => {
  const [params, setParams] = useState({ status: "1" });
  const [data, setData] = useState([]);
  const [modalFilter, showModalFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);

  const onChange = (e) => {
    setParams({
      ...params,
      [e.target.name]: e.target.value
    });
  };

  const onFilter = () => {
    setLoading(true);
    filter(params)
      .then((resp) => {
        setData(resp);
        showModalFilter(false);
      })
      .finally(() => setLoading(false));
  };

  const onCancelSale = (nfce) => {
    setLoadingAction({ id: nfce.id, action: 'cancel' });
    cancelSale(nfce.sale)
      .then(() => {
        toast.success('NFCe Cancelada com sucesso!', { position: "top-center" });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch(error => {
        const msg = Array.isArray(error) ? error[0] : error;
        if (msg === 'A sale must be confirmed (status = 2) to be cancelled.') {
          toast.error('A NFCe ja está autorizada.', { position: "top-center" });
        } else {
          toast.error(msg, { position: "top-center" });
        }
      })
      .finally(() => setLoadingAction(null));
  };

  const downloadPDF = (nfce) => {
    setLoadingAction({ id: nfce.id, action: 'pdf' });
    getNFeBase64({
      key: nfce.key,
      document: "nfce"
    }).then(resp => {
      const LinkSource = `data:application/pdf;base64,${resp.pdf}`;
      const DownloadLink = document.createElement('a');
      DownloadLink.href = LinkSource;
      DownloadLink.download = `${nfce.key}.pdf`;
      DownloadLink.click();
    }).finally(() => setLoadingAction(null));
  };

  const downloadXML = (nfce) => {
    setLoadingAction({ id: nfce.id, action: 'xml' });
    getNFeBase64({
      key: nfce.key,
      document: "nfce"
    }).then(resp => {
      const LinkSource = `data:application/xml;base64,${resp.xml}`;
      const DownloadLink = document.createElement('a');
      DownloadLink.href = LinkSource;
      DownloadLink.download = `${nfce.key}.xml`;
      DownloadLink.click();
    }).catch(error => {
      if (error.partner_error) {
        toast.error("Documento não localizado", { position: "top-center" });
      }
    }).finally(() => setLoadingAction(null));
  };

  useEffect(() => {
    onFilter();
  }, []);

  return (
    <Page 
      title="Listagem de NFC-e"
      headerAction={
        <div style={{ display: 'flex', gap: '1rem' }}>
          <DownloadReport icon request={filter} params={params} />
          <Button variant="secondary" onClick={() => showModalFilter(true)}>
            <i className="fas fa-filter" style={{ marginRight: '8px' }} />
            Filtrar Notas
          </Button>
        </div>
      }
    >

      <Table hover>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Situação</Table.HeaderCell>
            <Table.HeaderCell>Data da Venda</Table.HeaderCell>
            <Table.HeaderCell>Numeração</Table.HeaderCell>
            <Table.HeaderCell>Série</Table.HeaderCell>
            <Table.HeaderCell>Valor</Table.HeaderCell>
            <Table.HeaderCell>Cliente</Table.HeaderCell>
            <Table.HeaderCell style={{ textAlign: 'center' }}>Ações</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((nfce) => (
            <Table.Row key={nfce.id}>
              <Table.Cell>
                <Typography size="sm" style={{ fontWeight: 'bold', color: NFCE_STATUS_COLORS[nfce.status] }}>
                  {NFCE_STATUS_CHOICES[nfce.status]}
                </Typography>
              </Table.Cell>
              <Table.Cell>{formatDate(nfce.created_date)}</Table.Cell>
              <Table.Cell>{nfce.number}</Table.Cell>
              <Table.Cell>{nfce.series}</Table.Cell>
              <Table.Cell>{formatCurrencyValue(nfce.paid_value)}</Table.Cell>
              <Table.Cell>{nfce.client_name || 'Consumidor Final'}</Table.Cell>
              <Table.Cell style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <Tooltip content="Baixar PDF">
                    <Button 
                      variant="light" 
                      shape="rounded" 
                      size="sm" 
                      style={{ padding: '6px 10px', minWidth: 'auto', color: colors.info }}
                      onClick={() => downloadPDF(nfce)}
                      disabled={loadingAction && loadingAction.id === nfce.id}
                    >
                      <i className={loadingAction && loadingAction.id === nfce.id && loadingAction.action === 'pdf' ? "fas fa-spinner fa-spin" : "fas fa-file-pdf"} />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Baixar XML">
                    <Button 
                      variant="light" 
                      shape="rounded" 
                      size="sm" 
                      style={{ padding: '6px 10px', minWidth: 'auto', color: colors.warning }}
                      onClick={() => downloadXML(nfce)}
                      disabled={loadingAction && loadingAction.id === nfce.id}
                    >
                      <i className={loadingAction && loadingAction.id === nfce.id && loadingAction.action === 'xml' ? "fas fa-spinner fa-spin" : "fas fa-file-code"} />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Ver Venda">
                    <Button 
                      variant="light" 
                      shape="rounded" 
                      size="sm" 
                      style={{ padding: '6px 10px', minWidth: 'auto' }}
                      onClick={() => { window.location.href = `/sale/${nfce.sale}` }}
                    >
                      <i className="fas fa-eye" />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Cancelar NFCe">
                    <Button 
                      variant="light" 
                      shape="rounded" 
                      size="sm" 
                      style={{ padding: '6px 10px', minWidth: 'auto', color: colors.danger }}
                      onClick={() => onCancelSale(nfce)}
                      disabled={loadingAction && loadingAction.id === nfce.id}
                    >
                      <i className={loadingAction && loadingAction.id === nfce.id && loadingAction.action === 'cancel' ? "fas fa-spinner fa-spin" : "fas fa-times"} />
                    </Button>
                  </Tooltip>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
          {data.length === 0 && !loading && (
            <Table.Row>
              <Table.Cell colSpan={7} style={{ textAlign: 'center', padding: '3rem' }}>
                <Typography variant="paragraph">Nenhuma nota encontrada.</Typography>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <Modal title='Filtro NFC-e' show={modalFilter} onHide={() => showModalFilter(false)}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', padding: '1rem' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <Select 
              label="Situação" 
              name="status" 
              value={params.status !== undefined ? params.status : "1"} 
              onChange={onChange} 
              options={NFCE_STATUS_OPTIONS}
            />
          </div>
          <Input 
            label="Nome do Cliente" 
            name="client" 
            value={params.client || ""}
            onChange={onChange} 
            placeholder='Nome ou Razão Social' 
          />
          <Input 
            label="Série" 
            name="series" 
            value={params.series || ""}
            onChange={onChange} 
            type="number" 
            placeholder='Ex: 1' 
          />
          <Input 
            label="Número Inicial" 
            name="start_number" 
            value={params.start_number || ""}
            onChange={onChange} 
            type="number" 
            placeholder='01' 
          />
          <Input 
            label="Número Final" 
            name="end_number" 
            value={params.end_number || ""}
            onChange={onChange} 
            type="number" 
            placeholder='99' 
          />
          <Input 
            label="Data Inicial" 
            name="start_date" 
            value={params.start_date || ""}
            onChange={onChange} 
            type="date" 
          />
          <Input 
            label="Data Final" 
            name="end_date" 
            value={params.end_date || ""}
            onChange={onChange} 
            type="date" 
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: '500', color: '#111827', marginBottom: '8px' }}>Limite de resultados</label>
            <select
              name="end_count"
              onChange={onChange}
              className="form-control"
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '1rem',
                fontWeight: '400',
                lineHeight: '1.5',
                color: '#495057',
                backgroundColor: '#fff',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem',
                height: 'calc(1.5em + 0.75rem + 2px)'
              }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="300">300</option>
              <option value="500">500</option>
            </select>
          </div>
        </div>
        <div style={{ padding: '0 1rem 1rem', display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button onClick={onFilter}>
            Filtrar
          </Button>
        </div>
      </Modal>
    </Page>
  );
};

export default NFCeList;




// WEBPACK FOOTER //
// ./src/views/Fiscal/NFCe/index.jsx